import { FC } from 'react';

import { Toggle } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ProductSearchItem } from '@telialt/core-search/src/model';

import { AddProductPlaceholder } from '../AddProductPlaceholder/AddProductPlaceholder';
import { CompareProductCard } from '../CompareProductCard/CompareProductCard';
import {
    ComparisonTableHeadCategoryName,
    ComparisonTableHeadProductCell,
    ComparisonTableHeadTitle,
    ComparisonTableHeadTitleCell,
    ComparisonTableProductContainer,
    DifferencesOnlyToggle,
    MobileDifferencesOnlyRow,
} from './ComparisonTable.styles';

type Props = {
    categoryPath: string;
    categoryName?: string;
    isHeaderMinimal: boolean;
    showDifferencesOnly: boolean;
    onDifferencesOnlyChange: (isChecked: boolean) => void;
    isDifferencesOnlyDisabled?: boolean;
    productCodes: string[];
    products: ProductSearchItem[];
    productsLimit: number;
    onRemoveProduct: (productCode: string) => void;
};

export const ComparisonProductsRow: FC<Props> = ({
    categoryPath,
    categoryName,
    isHeaderMinimal,
    showDifferencesOnly,
    onDifferencesOnlyChange,
    isDifferencesOnlyDisabled,
    productCodes,
    products,
    productsLimit,
    onRemoveProduct,
}) => {
    const { t } = useTranslations();

    return (
        <>
            <tr>
                <ComparisonTableHeadTitleCell>
                    {!isHeaderMinimal && (
                        <ComparisonTableHeadTitle>
                            {t['eshop.comparison.table_head_title']}
                            {!!categoryName && !!products && products.length > 1 && (
                                <ComparisonTableHeadCategoryName>
                                    {products.length} {categoryName.toLowerCase()}
                                </ComparisonTableHeadCategoryName>
                            )}
                        </ComparisonTableHeadTitle>
                    )}
                    <DifferencesOnlyToggle
                        id="differences-only-toggle"
                        label={t['eshop.comparison.differences_only_desktop']}
                        checked={!isDifferencesOnlyDisabled && showDifferencesOnly}
                        disabled={isDifferencesOnlyDisabled}
                        onChange={onDifferencesOnlyChange}
                    />
                </ComparisonTableHeadTitleCell>
                {productCodes?.map((productCode) => {
                    const product = products.find(({ id }) => id === productCode);

                    return (
                        <ComparisonTableHeadProductCell key={product.id} scope="col">
                            <ComparisonTableProductContainer $isVisible={!isHeaderMinimal}>
                                <CompareProductCard
                                    variant="full"
                                    product={product}
                                    onRemoveProduct={() => onRemoveProduct(product.id)}
                                />
                            </ComparisonTableProductContainer>
                            <ComparisonTableProductContainer $isVisible={isHeaderMinimal}>
                                <CompareProductCard
                                    variant="minimal"
                                    product={product}
                                    onRemoveProduct={() => onRemoveProduct(product.id)}
                                />
                            </ComparisonTableProductContainer>
                        </ComparisonTableHeadProductCell>
                    );
                })}
                {products?.length < productsLimit &&
                    Array(productsLimit - products.length)
                        .fill(undefined)
                        .map((_, index) => (
                            <ComparisonTableHeadProductCell key={`placeholder-${index}`}>
                                <AddProductPlaceholder href={categoryPath} />
                            </ComparisonTableHeadProductCell>
                        ))}
            </tr>
            <MobileDifferencesOnlyRow>
                <td colSpan={productsLimit}>
                    <Toggle
                        id="mobile-differences-only-toggle"
                        label={t['eshop.comparison.differences_only_desktop']}
                        checked={!isDifferencesOnlyDisabled && showDifferencesOnly}
                        disabled={isDifferencesOnlyDisabled}
                        onChange={onDifferencesOnlyChange}
                    />
                </td>
            </MobileDifferencesOnlyRow>
        </>
    );
};
